<template>
  <vl-layer-tile
    :extent="extent"
    @mounted="imageLayerMounted"
    @postcompose="render"
    ref="baseLayer"
  >
    <vl-source-cytomine
      :projection="projectionName"
      :url="baseLayerURL"
      :tile-load-function="tileLoadFunction"
      :size="imageSize"
      :extent="extent"
      :nb-resolutions="image.zoom"
      ref="baseSource"
      @mounted="sourceMounted"
      :transition="0"
      :tile-size="[tileSize, tileSize]"
    />
  </vl-layer-tile>

</template>

<script>

import {normalizedLookUpTableFunc, VioletLUT} from '@/utils/color-manipulation.js';
import {DEFAULT_BOUNDS, sameHistogramBounds} from '@/utils/histogram-utils';
import {Cytomine} from 'cytomine-client';
import {get} from '@/utils/store-helpers';

export default {
  name: 'image-layer',
  props: {
    index: String,
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    extent() {
      return [0, 0, this.image.width, this.image.height];
    },
    imageSize() {
      return [this.image.width, this.image.height];
    },
    tileSize() {
      return this.image.tileSize;
    },
    projectionName() {
      return `CYTO-${this.image.id}`;
    },
    isHistologAcquireOrPreview() {
      return this.$store.getters[this.imageModule + 'isHistologAcquireOrPreview'];
    },

    /* Tile Requests */
    baseLayerProcessingParams() {
      return this.$store.getters[this.imageModule + 'tileRequestParams'];
    },
    baseLayerSliceParams() {
      return {
        zSlices: this.slices[0].zStack,
        timepoints: this.slices[0].time
      };
    },
    baseLayerURLQuery() {
      const query = new URLSearchParams({
        ...this.baseLayerSliceParams,
        ...this.baseLayerProcessingParams
      }).toString();
      if (query.length > 0) {
        return `?${query}`;
      }
      return query;
    },
    baseLayerURL() {
      const slice = this.slices[0];
      return `${Cytomine.instance.host + Cytomine.instance.basePath}sliceinstance/${slice.id}/normalized-tile/zoom/{z}/tx/{x}/ty/{y}.jpg${this.baseLayerURLQuery}`;
    },
    tileLoadFunction() {
      return (tile, src) => {
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.open('GET', src);
        xhr.setRequestHeader('Authorization', 'Bearer ' + this.shortTermToken);
        xhr.addEventListener('load', () => {
          const url = URL.createObjectURL(xhr.response);
          const tileImage = tile.getImage();
          tileImage.addEventListener('load', () => URL.revokeObjectURL(url));
          tileImage.src = url;
        });
        xhr.send();
      }
    },

    /* In browser rendering */
    inBrowserRendering() {
      return this.isHistologAcquireOrPreview && (this.applyColormap || this.applyContrast);
    },
    violetColormap() {
      return this.imageWrapper.colorOverlay.violetColormap;
    },
    applyColormap() {
      return this.violetColormap;
    },
    bounds() {
      return this.imageWrapper.colorOverlay.bounds;
    },
    applyContrast() {
      return !sameHistogramBounds(this.bounds, DEFAULT_BOUNDS);
    },
    contrastLUT() {
      if (!this.applyContrast) {
        return null;
      }

      return normalizedLookUpTableFunc(this.bounds.min, this.bounds.max);
    }
  },
  watch: {
    async violetColormap() {
      await this.refreshLayer();
    },
    bounds: {
      deep: true,
      handler: async function() {
        return await this.refreshLayer();
      }
    }
  },
  methods: {
    async sourceMounted() {
      await this.$refs.baseSource.$createPromise;
      this.$emit('sourceMounted', this.$refs.baseSource.$source);
    },
    async imageLayerMounted() {
      await this.$refs.baseLayer.$createPromise;
      this.$emit('mounted', this.$refs.baseLayer.$layer);
    },
    async refreshLayer() {
      await this.$refs.baseLayer.refresh();
    },
    render(e) {
      if (!this.inBrowserRendering) {
        return;
      }

      const ctx = e.context;
      const canvas = ctx.canvas;
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const dataLength = data.length;

      const applyContrast = this.applyContrast;
      const applyColormap = this.applyColormap;
      const contrastLUT = this.contrastLUT;
      const colormapLUT = VioletLUT;

      for (let i = 0; i < dataLength; i += 4) {
        let r = data[i];
        let g = data[i + 1];
        let b = data[i + 2];

        if (applyContrast) {
          r = contrastLUT[r];
          g = contrastLUT[g];
          b = contrastLUT[b];
        }

        if (applyColormap) {
          data[i] = colormapLUT[r * 3];
          data[i + 1] = colormapLUT[g * 3 + 1];
          data[i + 2] = colormapLUT[b * 3 + 2];
        } else {
          data[i] = r;
          data[i + 1] = g;
          data[i + 2] = b;
        }
      }

      ctx.putImageData(imageData, 0, 0);
    },
  },
};
</script>
